// src/App.jsx
import React, { useEffect } from 'react';
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate,
  createRoutesFromElements,
  createBrowserRouter,
  RouterProvider,
  Outlet
} from 'react-router-dom';
import Home from './pages/Home';
import PurchasePolicy from './pages/PurchasePolicy';
import Confidentiality from './pages/Confidentiality';
import LegalMentions from './pages/LegalMentions';
import NotarySearch from './pages/NotarySearch';
import Contact from './pages/Contact';
import About from './pages/About';
import HowItWorks from './pages/HowItWorks';
import Dashboard from './components/PlumPlusDash/Dashboard.jsx';
import Status from "./components/LandingPage-main/Status.jsx" 
import Login from './pages/Login.jsx';
import DivorceGuide from './components/PlumPlusDash/DivorceGuide.jsx';
import ProcedureSimulation from './components/PlumPlusDash/ProcedureSimulation.jsx';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import SeparationGuide from './pages/SeparationGuide.jsx';
import GardeGuide from './pages/GardeGuide.jsx';
import PensionGuide from './pages/PensionGuide.jsx';
import DivorceChecklist from './pages/DivorceChecklist.jsx';
import SeparationChecklist from './pages/SeparationChecklist.jsx';
import GardeChecklist from './pages/GardeChecklist.jsx';
import PensionChecklist from './pages/PensionChecklist.jsx';
import DivorceCalculator from './pages/DivorceCalculator.jsx';
import SeparationCalculator from './pages/SeparationCalculator.jsx';
import GardeCalculator from './pages/GardeCalculator.jsx';
import PensionCalculator from './pages/PensionCalculator.jsx';
import OrientationAideJuridique from './pages/OrientationAideJuridique.jsx';
import OrientationCabinets from './pages/OrientationCabinets.jsx';
import OrientationRessources from './pages/OrientationRessources.jsx';
import Timeline from './pages/DivorceTimeline.jsx';
import DelayCalculator from './pages/DelayCalculator.jsx';
import DashLayout from './components/PlumPlusDash/DashLayout.jsx';
import DivorceTimeline from './pages/DivorceTimeline.jsx';
import SepLegTimeline from './pages/SepLegTimeline.jsx';
import GardeTimeline from './pages/GardeTimeline.jsx';
import PensionTimeline from './pages/PensionTimeline.jsx';
import DivorcePensionCalculator from './pages/DivorcePensionCalculator';
import PaymentSuccess from './pages/PaymentSuccess.jsx';
import ProtectedRoute from './components/ProtectedRoute';
import SignUp from './pages/SignUp.jsx';  
import MagicLinkSignin from './pages/MagicLinkSignin';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import TermsOfUse from './pages/RefundPolicy';
import LegalNotice from './pages/LegalNotice';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import { pushToGit } from './utils/deployment';
import { useTranslation } from 'react-i18next';
import { SupabaseAuthProvider } from './contexts/SupabaseAuthContext';
import FeedbackButton from './components/PlumPlusDash/FeedbackButton';
import ClicoSearch from './components/PlumPlusDash/ClicoSearch.jsx';
import { initSecurity } from './utils/securityUtils';
import ConsentBanner from './components/ConsentBanner';
import DashboardAuth from './components/PlumPlusDash/DashboardAuth.jsx';
import DashboardProtectedRoute from './components/PlumPlusDash/DashboardProtectedRoute.jsx';
import { setupDevAuthInterceptor } from './utils/mockAuth';

// Supprimer les logs en production et activer les mesures de sécurité
if (process.env.NODE_ENV === 'production') {
  // Les fonctions de console seront remplacées par des fonctions vides
  // via initSecurity() plus bas
  
  // Suppression de la source des scripts pour éviter l'inspection
  document.addEventListener('DOMContentLoaded', () => {
    // Rendre invisible l'arborescence des fichiers
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach(script => {
      const originalSrc = script.getAttribute('src');
      if (originalSrc) {
        // Stocke l'original dans une variable non-accessible directement
        const hiddenSrc = originalSrc;
        script.removeAttribute('src');
        
        // Charge dynamiquement le script sans exposer le chemin
        fetch(hiddenSrc)
          .then(response => response.text())
          .then(content => {
            const newScript = document.createElement('script');
            newScript.textContent = content;
            script.replaceWith(newScript);
          })
          .catch(() => {
            // Silencieux en cas d'erreur
          });
      }
    });
  });
}

// Configurer l'intercepteur d'authentification en mode développement
if (process.env.NODE_ENV !== 'production') {
  setupDevAuthInterceptor();
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={
      <SupabaseAuthProvider>
        <ErrorBoundary>
          <SpeedInsights />
          <Analytics />
          <ScrollToTop />
          <Outlet />
          <FeedbackButton />
          <ConsentBanner />
        </ErrorBoundary>
      </SupabaseAuthProvider>
    }>
      {/* Public authentication routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/magiclink" element={<MagicLinkSignin />} />
      <Route path="/auth/callback" element={<Navigate to="/dashboard" replace />} />
      
      {/* Dashboard Authentication */}
      <Route path="/dashboard-auth" element={<DashboardAuth />} />
      
      {/* Public regular routes */}
      <Route path="/" element={<Home />} />
      <Route path="/purchase-policy" element={<PurchasePolicy />} />
      <Route path="/confidentiality" element={<Confidentiality />} />
      <Route path="/legal-mentions" element={<LegalMentions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/legal-notice" element={<LegalNotice />} />
      <Route path="/notary-search" element={<NotarySearch />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/about" element={<About />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/timeline/garde" element={<GardeTimeline />} />

      {/* Dashboard and related routes - protected with custom authentication */}
      <Route element={
        <DashboardProtectedRoute>
          <DashLayout />
        </DashboardProtectedRoute>
      }>
        <Route path="/dashboard" element={<Dashboard />} />
        {/* Timeline routes */}
        <Route path="/timeline" element={<Timeline />} />
        <Route path="/timeline/divorce" element={<DivorceTimeline />} />
        <Route path="/timeline/separation" element={<SepLegTimeline />} />
        <Route path="/timeline/pension" element={<PensionTimeline />} />
        {/* ClicoSearch - ajouté avec sa propre protection par mot de passe */}
        <Route path="/clico-search" element={<ClicoSearch />} />
        {/* Other previously protected routes */}
        <Route path="/guides/*" element={<DivorceGuide />} />
        <Route path="/guides/divorce" element={<DivorceGuide />} />
        <Route path="/guides/separation" element={<SeparationGuide />} />
        <Route path="/guides/garde" element={<GardeGuide />} />
        <Route path="/guides/pension" element={<PensionGuide />} />
        <Route path="/checklists/divorce" element={<DivorceChecklist />} />
        <Route path="/checklists/separation" element={<SeparationChecklist />} />
        <Route path="/checklists/garde" element={<GardeChecklist />} />
        <Route path="/checklists/pension" element={<PensionChecklist />} />
        <Route path="/calculators/divorce" element={<DivorceCalculator />} />
        <Route path="/calculators/separation" element={<SeparationCalculator />} />
        <Route path="/calculators/garde" element={<GardeCalculator />} />
        <Route path="/calculators/pension" element={<PensionCalculator />} />
        <Route path="/calculators/divorce-pension" element={<DivorcePensionCalculator />} />
        <Route path="/orientation/aide-juridique" element={<OrientationAideJuridique />} />
        <Route path="/orientation/cabinets" element={<OrientationCabinets />} />
        <Route path="/orientation/ressources" element={<OrientationRessources />} />
        <Route path="/delays" element={<DelayCalculator />} />
        <Route path="/simulation" element={<ProcedureSimulation />} />
        <Route path="/status" element={<Status />} />
      </Route>

      {/* Redirect old refund-policy to new terms-of-use */}
      <Route path="/refund-policy" element={<Navigate to="/terms-of-use" replace />} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

function App() {
  const { i18n } = useTranslation();

  // Initialisation des mesures de sécurité
  useEffect(() => {
    initSecurity();
  }, []);

  // Force French as the default language on app load
  useEffect(() => {
    if (i18n.language !== 'fr') {
      i18n.changeLanguage('fr');
    }
  }, [i18n]);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      // Check for Cmd + Enter (Mac) or Ctrl + Enter (Windows)
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        try {
          await pushToGit();
          // Log seulement en développement
          if (process.env.NODE_ENV !== 'production') {
            console.log('Successfully pushed changes to Git');
          }
        } catch (error) {
          // Garde console.error même en production pour les erreurs critiques
          console.error('Failed to push changes:', error);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return <RouterProvider router={router} />;
}

export default App;
